import CloseIcon from "@mui/icons-material/Close";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { SESSION_RECORDING_MASK_CLASS } from "_shared/session-recording/session-recording-privacy";
import { CORRESPONDENCE_HEADER_COLOR } from "messaging/correspondence/_views/correspondence-layout";
import type { ReactElement, ReactNode } from "react";

// #region Styled Components
const ConversationTabs = styled(Tabs)(() => ({
  // MUI's 48px minimum, under one-line tabs 38px tall, left a strip between the tabs and the box.
  minHeight: 0,
  // A container, so the Shared files tab can drop its text when the row is narrow.
  containerName: "forum-tabs",
  containerType: "inline-size",
  gridRowStart: 1,
  "& .MuiTabs-flexContainer": {
    borderBottom: `1px solid #aaa`,
    height: "100%",
  },
  "& .MuiTab-root": {
    "&:not(:first-of-type)": {
      borderLeft: 0,
    },
    maxWidth: "unset !important",
  },
  "& .MuiTabs-indicator": {
    display: "none",
  },
}));
const ConversationTab = styled(Tab)(({ theme }) => ({
  // No minimum width: in a 320px pane the title is what the width is for, and "Reviewers" was cut to
  // "Reviewe..." to leave room for padding.
  minWidth: 0,
  // One line of sentence case at 13px. Two lines of capitals with a count beside them looked wrong
  // however the count was placed; a short name on one line needs no placing at all.
  minHeight: 0,
  padding: theme.spacing(1, 1.25),
  textTransform: "none",
  fontSize: "0.8125rem",
  fontWeight: 500,
  lineHeight: 1.4,
  backgroundColor: CORRESPONDENCE_HEADER_COLOR,
  borderTopLeftRadius: "4px",
  borderTopRightRadius: "4px",
  // Sized from its title, then grown or shrunk with the others: "All Stakeholders" takes more of the row
  // than "Reviewers", instead of each taking an equal share and the longer title being cut to fit.
  flex: "1 1 auto",
  border: `1px solid #aaa`,
  position: "relative",
  // The open tab drops its lower edge to merge with the pane under it.
  "&.Mui-selected": {
    borderBottom: "none",
  },
  "&:not(.Mui-selected)": {
    backgroundColor: "#e8e9ef",
  },
}));
/**
 * Sized to its label rather than stretched like a conversation tab: it is one fixed destination beside
 * however many conversations are open, and the conversations are what the row is for. In a narrow row
 * -- a 400px Correspondence drawer, a phone -- its text goes and the icon stays, so the conversation
 * names keep the room. Its `aria-label` keeps the name either way.
 */
const SharedFilesTab = styled(ConversationTab)(({ theme }) => ({
  flex: "0 1 auto",
  gap: theme.spacing(0.5),
  minHeight: 0,
  minWidth: 0,
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  "& .MuiTab-icon": {
    flexShrink: 0,
    marginRight: 0,
  },
}));
const SharedFilesTabText = styled("span")(() => ({
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "@container forum-tabs (max-width: 599px)": {
    display: "none",
  },
}));
const TabLabel = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(0.5),
  justifyContent: "center",
  width: "100%",
}));
/**
 * The tab's short name on one line, ended with an ellipsis rather than wrapped if the row is too narrow
 * for it; the whole of the conversation's title shows under the pointer and is the tab's accessible
 * name. The title once wrapped onto two lines of capitals, which is what every placement of the count
 * beside it fought with.
 */
const TabTitle = styled("span")(() => ({
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
/** The unread count, beside the title on its line. */
const UnreadBadge = styled(Badge)(() => ({
  flexShrink: 0,
  "& .MuiBadge-badge": {
    fontSize: "0.65rem",
    height: 16,
    minWidth: 16,
    padding: "0 4px",
    position: "static",
    transform: "none",
  },
}));
/**
 * Sits at the end of the tab row, beside the member list, rather than inside a conversation tab: there it
 * read as closing that one tab, and it shared the tab's disabled state, so a locked chat could not be
 * closed at all.
 */
const CloseChatButtonSlot = styled("span")(() => ({
  alignItems: "center",
  alignSelf: "stretch",
  borderBottom: "1px solid #aaa",
  display: "flex",
}));
const CloseChatIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.primary,
  "&:hover": {
    color: theme.palette.primary.dark,
  },
}));
// #endregion

/** The tab value of the Shared files tab. A conversation tab's value is its own id, which this can never be. */
export const SHARED_FILES_TAB = "shared-files";

/** One conversation the tab row offers. */
export type CorrespondenceConversationTab = {
  /** What the row reports back when this tab is picked. Never {@link SHARED_FILES_TAB}. */
  value: string;
  /** Who the conversation is with: "All Stakeholders", "Client Team", a person's name. */
  title: string;
  /**
   * What the tab shows, when the title is longer than the tab needs: "Work" for "Work Correspondence"
   * in a pane already headed Correspondence. It must be the start of the title, which stays the tab's
   * accessible name.
   */
  shortTitle?: string;
  /** How many messages in it the reader has not read. The tab shows a count only when it is above 0. */
  unreadCount?: number;
  disabled?: boolean;
};

type CorrespondenceTabsProps = {
  /** The open tab: a conversation's value, {@link SHARED_FILES_TAB}, or false while there is nothing to open. */
  value: string | false;
  conversations: CorrespondenceConversationTab[];
  /** Shows the Shared files tab after the conversations. */
  showSharedFiles: boolean;
  sharedFilesDisabled?: boolean;
  /**
   * What the files tab is called, and its icon. Defaults to Shared files. A work agreement's pane
   * calls it Documents: it holds the work's own documents, not the chats' attachments (#6054).
   */
  filesTab?: { label: string; icon: ReactElement };
  /** What the row says when there is no conversation to open. */
  emptyLabel?: string;
  /**
   * Drawn at the end of the row, after the Shared files tab: the member list. The row hands its children
   * a tab's props, so this is a component that ignores them, never a bare DOM element.
   */
  trailing?: ReactNode;
  /** Offers a "Close chat" button at the very end of the row, which closes the whole window. */
  onClose?: () => void;
  onChange: (value: string) => void;
};

/**
 * The tab row across the top of a Correspondence pane: one tab per conversation, then Shared files, then
 * the member list.
 *
 * The work agreement's chat and the proposal's comments draw the same row, so that a reader who moves
 * from a proposal to the agreement it became finds the same controls in the same places. A
 * conversation tab is a forum on the agreement's side and an audience on the proposal's, so the row
 * takes plain options rather than forums.
 */
export default function CorrespondenceTabs(props: Readonly<CorrespondenceTabsProps>) {
  const { value, conversations, showSharedFiles, sharedFilesDisabled, emptyLabel, trailing, onClose, onChange } =
    props;
  const filesTabLabel = props.filesTab?.label ?? "Shared files";
  const filesTabIcon = props.filesTab?.icon ?? <FolderSharedOutlinedIcon fontSize="small" />;
  // Every tab takes an equal share of the row, and the Shared files tab is one of them. The
  // `maxWidth: unset !important` on the row overrides this today; the count is kept right so the cap
  // is correct if that rule goes.
  const tabMaxWidth = `calc((100% / ${conversations.length + 1}) - 48px)`;

  return (
    <ConversationTabs value={value} onChange={(_, newValue: string) => onChange(newValue)} textColor="primary">
      {conversations.length === 0 && <ConversationTab disabled label={emptyLabel ?? "No Forums Found"} value="none" />}
      {conversations.map((conversation) => (
        <ConversationTab
          key={conversation.value}
          disabled={conversation.disabled}
          value={conversation.value}
          // Also on click, as the chat window always did: the row reports only a change of value, and a
          // press on the open tab must still reach a host that acts on it.
          onClick={() => onChange(conversation.value)}
          style={{ maxWidth: tabMaxWidth }}
          // A conversation's title is data: a forum's name, a proposal's, or the people in it (#6330).
          className={SESSION_RECORDING_MASK_CLASS}
          aria-label={conversationTabName(conversation)}
          label={<ConversationTabLabel conversation={conversation} />}
        />
      ))}
      {showSharedFiles && (
        <SharedFilesTab
          aria-label={filesTabLabel}
          disabled={sharedFilesDisabled}
          icon={filesTabIcon}
          iconPosition="start"
          label={<SharedFilesTabText>{filesTabLabel}</SharedFilesTabText>}
          style={{ maxWidth: tabMaxWidth }}
          title={filesTabLabel}
          value={SHARED_FILES_TAB}
        />
      )}
      {trailing}
      {onClose && <CloseChatButton onClose={onClose} />}
    </ConversationTabs>
  );
}

// A component rather than a styled element inline in the tab row: Tabs hands every child its tab
// props, which a DOM element would pass on as unknown attributes.
function CloseChatButton(props: Readonly<{ onClose: () => void }>) {
  const { onClose } = props;
  return (
    <CloseChatButtonSlot>
      <Tooltip title="Close chat">
        <CloseChatIconButton aria-label="Close chat" onClick={onClose}>
          <CloseIcon />
        </CloseChatIconButton>
      </Tooltip>
    </CloseChatButtonSlot>
  );
}

function ConversationTabLabel(props: Readonly<{ conversation: CorrespondenceConversationTab }>) {
  const { title, shortTitle, unreadCount } = props.conversation;
  const unread = unreadCount ?? 0;

  return (
    <TabLabel>
      <TabTitle title={title}>{shortTitle ?? title}</TabTitle>
      {unread > 0 && (
        <UnreadBadge badgeContent={unread} color="primary" data-testid="correspondence-tab-unread" />
      )}
    </TabLabel>
  );
}

/**
 * What a screen reader hears for a tab: the conversation's whole title, then its count. The short name
 * on screen is the start of the whole title, so the visible label stays within the accessible one.
 */
function conversationTabName(conversation: CorrespondenceConversationTab): string {
  const unread = conversation.unreadCount ?? 0;
  return unread > 0 ? `${conversation.title}, ${unread} unread` : conversation.title;
}
